/**
 * "Payments & invoices" (2026-10-20): import payments or invoices from a CSV (a mandatory preview first), the rows
 * that need a person to match them, the day-range reconciliation, and the CSV export. Every amount is shown from
 * integer cents. eMerge never matches a row by name; the resident's eMerge id is the key.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shortDate, FinanceScope } from './financeApi';
import {
  ledgerApi, writeFailure, centsToDollars, todayEastern, OUTCOMES, OUTCOME_LABEL,
  ImportKind, ImportPreview, ImportResult, ImportRow, NeedsMatchItem, Reconciliation, RowOutcome,
} from './ledgerApi';
import { StateLegend } from './LedgerBilling';

const OUTCOME_LEGEND = OUTCOMES.map((o) => ({ key: o.toLowerCase(), word: OUTCOME_LABEL[o] }));

const COLUMNS: Record<ImportKind, string> = {
  PAYMENTS: 'resident_id (the eMerge id), invoice_number, amount, received_on, method, reference, memo, name',
  INVOICES: 'resident_id (the eMerge id), invoice_number, issued_on, due_on, amount, description',
};

/** Thirty-one days back from a YYYY-MM-DD, by UTC date arithmetic. */
function daysBefore(day: string, n: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - n * 86400000);
  return d.toISOString().slice(0, 10);
}

function Counts({ counts }: { counts: Record<RowOutcome, number> }) {
  return (
    <ul className="lg-legend lg-counts" aria-label="Rows by outcome">
      {OUTCOMES.map((o) => <li key={o}><span className={`lg-swatch lg-s-${o.toLowerCase()}`} aria-hidden="true" /> {OUTCOME_LABEL[o]}: <strong>{counts && counts[o] !== undefined ? counts[o] : 0}</strong></li>)}
    </ul>
  );
}

function RowsTable({ rows, label }: { rows: ImportRow[]; label: string }) {
  if (!rows.length) return <p className="um-quiet">No rows.</p>;
  return (
    <div className="lg-tablewrap">
      <table className="lg-table" aria-label={label}>
        <thead><tr><th>Row</th><th>Outcome</th><th>Resident</th><th>Invoice</th><th className="num">Amount</th><th>Day</th><th>Method</th><th>What happened</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row_number}>
              <td>{r.row_number}</td>
              <td><span className={`lg-chip lg-chip--${r.outcome.toLowerCase()}`}>{OUTCOME_LABEL[r.outcome] || r.outcome}</span></td>
              <td>{r.resident ? r.resident.name : '—'}</td>
              <td>{r.invoice_number || '—'}</td>
              <td className="num">{r.amount_cents === null || r.amount_cents === undefined ? '—' : centsToDollars(r.amount_cents)}</td>
              <td>{r.day ? shortDate(r.day) : '—'}</td>
              <td>{r.method || '—'}{r.reference ? <span className="fn-source"> · {r.reference}</span> : null}</td>
              <td>{r.message || '—'}{r.import_row_id ? <span className="fn-source"> · row id {r.import_row_id}</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImportSection({ onImported }: { onImported: () => void }) {
  const [kind, setKind] = useState<ImportKind>('PAYMENTS');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState<null | 'preview' | 'confirm'>(null);
  const [err, setErr] = useState<string | null>(null);
  // A preview belongs to the exact text and kind it read; changing either clears it, so Confirm can never send other text.
  const change = (next: { kind?: ImportKind; text?: string; file?: string | null }) => {
    if (next.kind !== undefined) setKind(next.kind);
    if (next.text !== undefined) setText(next.text);
    if (next.file !== undefined) setFileName(next.file);
    setPreview(null); setResult(null); setErr(null);
  };
  const onFile = (f: File | undefined) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => change({ text: String(reader.result || ''), file: f.name });
    reader.onerror = () => setErr('Could not read that file.');
    reader.readAsText(f);
  };
  const doPreview = async () => {
    setBusy('preview'); setErr(null); setResult(null);
    try {
      const r = await ledgerApi.previewImport(kind, text);
      if (!r.success || !r.data) { setPreview(null); setErr(writeFailure(r).message); return; }
      setPreview(r.data);
    } catch { setErr('Could not reach the server. Nothing was imported.'); } finally { setBusy(null); }
  };
  const doConfirm = async () => {
    if (!preview) return;
    setBusy('confirm'); setErr(null);
    try {
      const r = await ledgerApi.confirmImport(kind, text, preview.file_sha256);
      if (!r.success || !r.data) { setErr(writeFailure(r).message); return; }
      setResult(r.data); setPreview(null); onImported();
    } catch { setErr('Could not reach the server; it may or may not have been imported. Preview the same file again to see.'); } finally { setBusy(null); }
  };
  return (
    <section className="um-card lg-card" aria-label="Import">
      <div className="um-card-head"><div><h2>Import</h2><p className="why">Bring in payments or invoices from a CSV file. You always see a preview first, and nothing is recorded until you confirm it. Residents are matched by their eMerge id, never by name.</p></div></div>
      <form className="um-form lg-form" aria-label="Import a file" onSubmit={(e) => { e.preventDefault(); void doPreview(); }}>
        <label><span>What is in the file</span>
          <select value={kind} onChange={(e) => change({ kind: e.target.value as ImportKind })}>
            <option value="PAYMENTS">Payments</option><option value="INVOICES">Invoices</option>
          </select>
        </label>
        <p className="um-quiet">Columns: {COLUMNS[kind]}. {kind === 'PAYMENTS' ? 'The name column is only to help a person match a row; eMerge never matches by name.' : ''}</p>
        <label><span>Choose a .csv file</span><input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={(e) => onFile(e.target.files ? e.target.files[0] : undefined)} /></label>
        {fileName && <p className="um-quiet">File: {fileName}</p>}
        <label><span>Or paste the CSV here</span><textarea value={text} onChange={(e) => change({ text: e.target.value, file: null })} rows={5} aria-label="CSV text" /></label>
        <div className="btns">
          <button type="submit" className="um-open" disabled={busy !== null || !text.trim()}>{busy === 'preview' ? 'Reading…' : 'Preview'}</button>
          <button type="button" className="um-open um-primary" disabled={busy !== null || !preview} onClick={doConfirm}>{busy === 'confirm' ? 'Importing…' : 'Confirm import'}</button>
        </div>
        {!preview && !result && <p className="um-quiet">Confirm turns on after you preview this file.</p>}
        {err && <p className="um-notice stop" role="alert">{err}</p>}
      </form>
      <StateLegend items={OUTCOME_LEGEND} label="Row outcomes" />
      {preview && (
        <div className="lg-result" aria-label="Preview">
          <p><strong>Preview:</strong> {preview.row_count} row{preview.row_count === 1 ? '' : 's'}. Nothing has been recorded yet.</p>
          <Counts counts={preview.counts} />
          <RowsTable rows={preview.rows} label="Preview rows" />
        </div>
      )}
      {result && (
        <div className="lg-result" aria-label="Import result">
          <p className="um-notice ok" role="status">
            {result.replayed ? 'This file was already imported; nothing new was recorded except rows not written before.' : `Imported ${result.row_count} row${result.row_count === 1 ? '' : 's'}.`}
          </p>
          <Counts counts={result.counts} />
          <RowsTable rows={result.rows} label="Import rows" />
        </div>
      )}
    </section>
  );
}

function NeedsMatchSection({ nonce }: { nonce: number }) {
  const [items, setItems] = useState<NeedsMatchItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await ledgerApi.needsMatch();
        if (!live) return;
        if (r.success && r.data) { setItems(r.data.items || []); setErr(null); return; }
        setItems(null); setErr(writeFailure(r).message);
      } catch { if (live) { setItems(null); setErr('Could not reach the server.'); } }
    })();
    return () => { live = false; };
  }, [nonce]);
  return (
    <section className="um-card lg-card" aria-label="Needs a match">
      <div className="um-card-head"><div><h2>Needs a match</h2><p className="why">Imported rows eMerge could not apply on its own. Open the resident, then Record payment for a payments row, or Record invoice for an invoices row, and paste this row&rsquo;s id into &ldquo;Matching import row&rdquo;.</p></div></div>
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err}</p>}
      {!err && items === null && <p className="um-quiet" style={{ padding: 12 }}>Loading…</p>}
      {items && items.length === 0 && <p className="um-quiet" style={{ padding: 12 }}>Nothing needs a match.</p>}
      {items && items.length > 0 && (
        <ul className="fn-notes lg-match" aria-label="Rows that need a match">
          {items.map((i) => (
            <li key={i.import_row_id}>
              <strong>Row {i.row_number}</strong> · {i.kind === 'PAYMENTS' ? 'payment' : 'invoice'} · imported {shortDate(i.imported_at)}{i.imported_by_name ? ` by ${i.imported_by_name}` : ''}
              <br /><strong>{i.kind === 'PAYMENTS' ? 'Use Record payment for this row.' : 'Use Record invoice for this row.'}</strong> <span>{i.message || i.code || 'Needs a person to match it.'}</span>
              <br /><span className="fn-source">{Object.entries(i.cells || {}).filter(([, v]) => v !== '').map(([k, v]) => `${k}: ${v}`).join(' · ')}</span>
              <br /><span className="fn-source">Row id <code>{i.import_row_id}</code></span>
              {i.resident && <> · <Link to={`/portal/finance?view=search&resident=${i.resident.id}`}>Open {i.resident.name}</Link></>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReconcileSection({ canExport }: { canExport: boolean }) {
  const [to, setTo] = useState(todayEastern());
  const [from, setFrom] = useState(daysBefore(todayEastern(), 31));
  const [data, setData] = useState<Reconciliation | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [xkind, setXkind] = useState<'payments' | 'invoices'>('payments');
  const [xerr, setXerr] = useState<string | null>(null);
  const [xbusy, setXbusy] = useState(false);
  const load = useCallback(async (f: string, t: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await ledgerApi.reconciliation(f, t);
      if (!r.success || !r.data) { setData(null); setErr(writeFailure(r).message); return; }
      setData(r.data);
    } catch { setData(null); setErr('Could not reach the server.'); } finally { setBusy(false); }
  }, []);
  useEffect(() => { void load(from, to); /* the first look is the default range; after that, Show */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doExport = async () => {
    setXbusy(true); setXerr(null);
    try { setXerr(await ledgerApi.exportCsv(xkind, from, to)); } catch { setXerr('Could not download the file. Try again in a moment.'); } finally { setXbusy(false); }
  };
  return (
    <section className="um-card lg-card" aria-label="Reconcile">
      <div className="um-card-head"><div><h2>Reconcile</h2><p className="why">Every payment recorded in eMerge between two days, by how it was paid and by who recorded it, with the voids and what is still open.</p></div></div>
      <form className="um-form lg-form lg-inline" aria-label="Reconciliation range" onSubmit={(e) => { e.preventDefault(); void load(from, to); }}>
        <label><span>From</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From day" /></label>
        <label><span>To</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To day" /></label>
        <div className="btns"><button type="submit" className="um-open" disabled={busy || !from || !to}>{busy ? 'Reading…' : 'Show'}</button></div>
      </form>
      {canExport && (
        <form className="um-form lg-form lg-inline" aria-label="Export" onSubmit={(e) => { e.preventDefault(); void doExport(); }}>
          <label><span>Export for these days</span>
            <select value={xkind} onChange={(e) => setXkind(e.target.value as 'payments' | 'invoices')} aria-label="Export kind"><option value="payments">Payments</option><option value="invoices">Invoices</option></select>
          </label>
          <div className="btns"><button type="submit" className="um-open" disabled={xbusy || !from || !to}>{xbusy ? 'Preparing…' : 'Download CSV'}</button></div>
          {xerr && <p className="um-notice stop" role="alert">{xerr}</p>}
        </form>
      )}
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err}</p>}
      {data && !data.available && <p className="um-quiet" style={{ padding: 12 }}>The billing ledger is not installed on this server yet.</p>}
      {data && data.available && (
        <div className="lg-result">
          <h3>Payments {shortDate(data.from)} to {shortDate(data.to)}: {data.payments.count} totaling {centsToDollars(data.payments.total_cents)}</h3>
          {data.payments.by_method.length > 0 && (
            <table className="lg-table" aria-label="Payments by method">
              <thead><tr><th>Method</th><th className="num">Payments</th><th className="num">Total</th></tr></thead>
              <tbody>{data.payments.by_method.map((m) => <tr key={m.method}><td>{m.label}</td><td className="num">{m.count}</td><td className="num">{centsToDollars(m.total_cents)}</td></tr>)}</tbody>
            </table>
          )}
          {data.payments.items.length === 0 ? <p className="um-quiet">No payment was recorded in these days.</p> : (
            <div className="lg-tablewrap">
              <table className="lg-table" aria-label="Payments recorded">
                <thead><tr><th>Received</th><th>Resident</th><th>Building</th><th className="num">Amount</th><th>Method</th><th>Reference</th><th>Applied to</th><th>State</th><th>Recorded by</th></tr></thead>
                <tbody>
                  {data.payments.items.map((p) => (
                    <tr key={p.id} className={p.state === 'VOIDED' ? 'lg-void' : ''}>
                      <td>{shortDate(p.received_on)}</td><td>{p.resident.name}</td><td>{p.facility_name || '—'}</td><td className="num">{centsToDollars(p.amount_cents)}</td>
                      <td>{p.method_label}</td><td>{p.reference || '—'}</td><td>{p.invoice_number || 'Opening balance'}</td>
                      <td><span className={`lg-chip lg-chip--${p.state.toLowerCase()}`}>{p.state === 'VOIDED' ? 'Voided' : 'Recorded'}</span></td>
                      <td>{p.recorded_by_name || '—'}<span className="fn-source"> · {shortDate(p.recorded_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h3>Voids: {data.voids.count}</h3>
          {data.voids.items.length > 0 && (
            <table className="lg-table" aria-label="Voids">
              <thead><tr><th>Voided</th><th>Resident</th><th>What</th><th className="num">Amount</th><th>Why</th><th>By</th></tr></thead>
              <tbody>{data.voids.items.map((v) => <tr key={v.id}><td>{shortDate(v.voided_at)}</td><td>{v.resident.name}</td><td>{v.kind === 'PAYMENT' ? 'Payment' : v.charge_kind === 'OPENING_BALANCE' ? 'Opening balance' : `Invoice ${v.invoice_number || ''}`}</td><td className="num">{centsToDollars(v.amount_cents)}</td><td>{v.reason}</td><td>{v.voided_by_name || '—'}</td></tr>)}</tbody>
            </table>
          )}
          <h3>Invoices issued in these days: {data.invoices_issued.count} totaling {centsToDollars(data.invoices_issued.total_cents)}</h3>
          <h3>Still open: {data.invoices_open.count} totaling {centsToDollars(data.invoices_open.open_cents)}</h3>
          {data.invoices_open.items.length > 0 && (
            <div className="lg-tablewrap">
              <table className="lg-table" aria-label="Open invoices">
                <thead><tr><th>Resident</th><th>Building</th><th>Invoice</th><th>Issued</th><th>Due</th><th className="num">Total</th><th className="num">Open</th></tr></thead>
                <tbody>{data.invoices_open.items.map((i) => <tr key={i.id}><td>{i.resident.name}</td><td>{i.facility_name || '—'}</td><td>{i.kind === 'OPENING_BALANCE' ? 'Opening balance' : (i.invoice_number || '—')}</td><td>{shortDate(i.issued_on)}</td><td>{shortDate(i.due_on)}</td><td className="num">{centsToDollars(i.total_cents)}</td><td className="num">{centsToDollars(i.open_cents)}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function LedgerImportPage({ scope }: { scope: FinanceScope }) {
  const can = scope.can || ({} as FinanceScope['can']);
  const [nonce, setNonce] = useState(0);
  return (
    <div className="lg-page">
      {can.import_ledger && <ImportSection onImported={() => setNonce((n) => n + 1)} />}
      {!can.import_ledger && <section className="um-card"><p className="um-quiet" style={{ padding: 12 }}>Importing a file is not open to your role. You can still reconcile{can.export_ledger ? ' and export' : ''}.</p></section>}
      {(can.import_ledger || can.record_payments) && <NeedsMatchSection nonce={nonce} />}
      <ReconcileSection canExport={Boolean(can.export_ledger)} />
    </div>
  );
}
